// The screens every simple app shares: the list (or board), a new record form, and the record page with its
// stages, fields and activity. What each app holds comes from its spec (./specs.ts).
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { request } from "../api";
import { useLoad } from "../hooks";
import { CommandBar } from "../Shell";
import { Icon } from "../icons";
import { ErrorBox, ago } from "../ui";
import { areaHref, findModule } from "../suite";
import { findSpec, type AppSpec, type Field, type Stage } from "./specs";
import { ModulePage } from "../pages/ModulePage";

/** "New new item request" reads twice: a noun that already starts with "new" gets one "New". */
const newLabel = (noun: string) => (/^new /i.test(noun) ? `New ${noun.slice(4)}` : `New ${noun}`);

type Value = string | number | boolean | null;
export type AppRecord = {
  id: string; app: string; number: string; title: string; stage: string; owner: string | null; fields: Record<string, Value>;
  createdBy: string; createdByName: string | null; createdAt: string; updatedAt: string;
};
type AppEvent = { id: string; kind: "created" | "stage" | "edit" | "comment"; body: string | null; detail: any; actorName: string | null; at: string };

const appsApi = {
  list: (app: string, q?: string) => request<AppRecord[]>("GET", `/api/apps/${app}/records${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  get: (n: string) => request<AppRecord & { events: AppEvent[] }>("GET", `/api/apps/r/${encodeURIComponent(n)}`),
  create: (app: string, body: unknown) => request<AppRecord>("POST", `/api/apps/${app}/records`, body),
  update: (n: string, body: unknown) => request<AppRecord>("PATCH", `/api/apps/r/${encodeURIComponent(n)}`, body),
  comment: (n: string, body: string) => request<AppEvent>("POST", `/api/apps/r/${encodeURIComponent(n)}/comments`, { body }),
};

// ----- values -----
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
function show(f: Field | undefined, v: Value): ReactNode {
  if (v === null || v === undefined || v === "") return <span className="muted">—</span>;
  if (!f) return String(v);
  switch (f.type) {
    case "money": return usd.format(Number(v));
    case "percent": return `${v}%`;
    case "yesno": return v === true || v === "Yes" ? "Yes" : "No";
    case "number": return `${Number(v).toLocaleString()}${f.unit ? ` ${f.unit}` : ""}`;
    case "date": return new Date(String(v) + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    case "bc": return <span className="mono">{String(v)}</span>;
    case "long": return <span style={{ whiteSpace: "pre-wrap" }}>{String(v)}</span>;
    default: return String(v);
  }
}
const toValue = (f: Field, raw: string): Value => {
  if (raw.trim() === "") return null;
  if (f.type === "number" || f.type === "money" || f.type === "percent") { const n = Number(raw); return Number.isFinite(n) ? n : null; }
  if (f.type === "yesno") return raw === "Yes";
  if (f.type === "bc") return raw.trim().toUpperCase();
  return raw;
};
const toRaw = (f: Field, v: Value) => v === null || v === undefined ? "" : f.type === "yesno" ? (v ? "Yes" : "No") : String(v);

function Input({ f, value, onChange, id }: { f: Field; value: string; onChange: (s: string) => void; id: string }) {
  if (f.type === "long") return <textarea id={id} className="textarea" rows={3} maxLength={f.maxLength} value={value} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "select" || f.type === "yesno") return (
    <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>{(f.type === "yesno" ? ["Yes", "No"] : f.options ?? []).map((o) => <option key={o}>{o}</option>)}
    </select>
  );
  const type = f.type === "date" ? "date" : ["number", "money", "percent"].includes(f.type) ? "number" : "text";
  return <input id={id} className={`input${f.type === "bc" ? " mono" : ""}`} type={type} step="any" value={value} onChange={(e) => onChange(e.target.value)}
    maxLength={type === "text" ? f.maxLength : undefined} min={type === "number" ? f.min : undefined} max={type === "number" ? f.max : undefined}
    placeholder={f.type === "bc" ? `${f.bc ?? "BC"} no.` : f.type === "person" ? "Name" : undefined} />;
}

function FieldRow({ f, children }: { f: Field; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={`f-${f.key}`}>{f.label}{f.required && <span className="muted"> *</span>}{f.unit && <span className="muted"> ({f.unit})</span>}{f.type === "bc" && <span className="sa-bc">Business Central</span>}</label>
      {children}
      {f.hint && <span className="hint">{f.hint}</span>}
    </div>
  );
}

const stageOf = (s: AppSpec, id: string): Stage => s.stages.find((x) => x.id === id) ?? { id, name: id };
const StagePill = ({ s, id }: { s: AppSpec; id: string }) => { const st = stageOf(s, id); return <span className={`status ${st.tone ?? ""}`}>{st.name}</span>; };
const colLabel = (s: AppSpec, k: string) => (k === "owner" ? "Owner" : s.fields.find((f) => f.key === k)?.label ?? k);
const colValue = (s: AppSpec, r: AppRecord, k: string) => (k === "owner" ? show({ key: "owner", label: "Owner", type: "person" }, r.owner) : show(s.fields.find((f) => f.key === k), r.fields[k] ?? null));
const plural = (n: string) => (/(s|x|ch)$/.test(n) ? `${n}es` : /y$/.test(n) && !/ay$/.test(n) ? `${n.slice(0, -1)}ies` : `${n}s`);

// ----- app home: list or board -----
export function SimpleAppPage() {
  const { id = "" } = useParams();
  const spec = findSpec(id);
  if (!spec) return <ModulePage />;
  return <AppHome spec={spec} />;
}

function AppHome({ spec }: { spec: AppSpec }) {
  const found = findModule(spec.id)!;
  const [params, setParams] = useSearchParams();
  const stage = params.get("stage") ?? "";
  const shape = params.get("as") === "board" && spec.board !== false ? "board" : "list";
  const [q, setQ] = useState("");
  const { data, error, reload } = useLoad(() => appsApi.list(spec.id), [spec.id]);
  const set = (k: string, v: string | null) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const counts = useMemo(() => { const c: Record<string, number> = {}; for (const r of data ?? []) c[r.stage] = (c[r.stage] ?? 0) + 1; return c; }, [data]);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data ?? []).filter((r) => (!stage || r.stage === stage) && (!t || [r.number, r.title, r.owner ?? "", ...Object.values(r.fields).map(String)].join(" ").toLowerCase().includes(t)));
  }, [data, stage, q]);
  const m = found.module;

  return (
    <>
      <CommandBar status={data ? `${data.length} ${data.length === 1 ? spec.noun : plural(spec.noun)}` : "Loading…"}>
        <Link className="cmd primary" to={`/m/${spec.id}/new`}><Icon name="plus" size={14} />{newLabel(spec.noun)}</Link>
        {spec.board !== false && <button className="cmd" onClick={() => set("as", shape === "board" ? null : "board")}>{shape === "board" ? "Show as list" : "Show as board"}</button>}
        <button className="cmd" onClick={() => reload()}>Refresh</button>
      </CommandBar>
      <div className="page">
        <div className="page-title">
          <nav className="crumbs" aria-label="Breadcrumb" style={{ flexBasis: "100%", margin: 0 }}><Link to="/">Home</Link><span>/</span><Link to={areaHref(found.domain)}>{found.domain.name}</Link><span>/</span><span>{m.name}</span></nav>
          <h1>{m.name}</h1><span className="sub">{m.summary}</span>
        </div>
        <div className="pivot" role="tablist">
          <button role="tab" aria-selected={stage === ""} onClick={() => set("stage", null)}>All <span className="muted">{data?.length ?? ""}</span></button>
          {spec.stages.map((s) => <button key={s.id} role="tab" aria-selected={stage === s.id} onClick={() => set("stage", s.id)}>{s.name} <span className="muted">{counts[s.id] ?? 0}</span></button>)}
        </div>
        <div className="filters" style={{ padding: 0 }}>
          <input className="input" style={{ maxWidth: 420 }} type="search" aria-label="Filter" placeholder={`Filter ${plural(spec.noun)}`} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <ErrorBox error={error} />
        {!data ? <section className="card"><div className="empty">Loading…</div></section>
          : !data.length ? <EmptyApp spec={spec} />
          : shape === "board" ? (
            <div className="board sa-board" style={{ gridTemplateColumns: `repeat(${spec.stages.length}, minmax(220px, 1fr))` }}>
              {spec.stages.map((s) => {
                const col = rows.filter((r) => r.stage === s.id);
                return (
                  <section key={s.id} className="board-col" aria-label={s.name}>
                    <div className="board-h"><span className="nm">{s.name}</span><span className="ct">{col.length}</span></div>
                    {col.map((r) => (
                      <Link key={r.id} to={`/m/${spec.id}/${r.number}`} className="deal">
                        <span className="deal-n">{r.title}</span>
                        <span className="deal-a">{r.number}{spec.columns[0] && spec.columns[0] !== "owner" && r.fields[spec.columns[0]] != null ? <> · {colValue(spec, r, spec.columns[0])}</> : null}</span>
                        <span className="deal-row"><span className="deal-q">{r.owner ?? "Unassigned"}</span><span className="deal-d">{ago(r.updatedAt)}</span></span>
                      </Link>
                    ))}
                  </section>
                );
              })}
            </div>
          ) : (
            <section className="card">
              {!rows.length ? <div className="empty">Nothing matches.</div> : (
                <div className="table-wrap">
                  <table className="dgrid">
                    <thead><tr><th>No.</th><th>{spec.title}</th>{spec.columns.map((k) => <th key={k}>{colLabel(spec, k)}</th>)}<th>Stage</th><th>Updated</th></tr></thead>
                    <tbody>{rows.map((r) => (
                      <tr key={r.id}>
                        <td><Link className="num" to={`/m/${spec.id}/${r.number}`}>{r.number}</Link></td>
                        <td className="title" title={r.title}>{r.title}</td>
                        {spec.columns.map((k) => <td key={k}>{colValue(spec, r, k)}</td>)}
                        <td><StagePill s={spec} id={r.stage} /></td>
                        <td className="muted" style={{ whiteSpace: "nowrap" }}>{ago(r.updatedAt)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        <AboutApp spec={spec} />
      </div>
    </>
  );
}

function EmptyApp({ spec }: { spec: AppSpec }) {
  return (
    <section className="card"><div className="empty">
      <p style={{ margin: "0 0 12px" }}>No {plural(spec.noun)} yet.</p>
      <Link className="btn primary" to={`/m/${spec.id}/new`}><Icon name="plus" size={14} />{newLabel(spec.noun)}</Link>
    </div></section>
  );
}

/** What the app is for and how it's set up, so it's easy to say what should change. */
function AboutApp({ spec }: { spec: AppSpec }) {
  const { module: m } = findModule(spec.id)!;
  return (
    <details className="card sa-about">
      <summary>About this app</summary>
      <div className="card-body">
        {m.does?.length ? <><h3>What it does</h3><ul>{m.does.map((d) => <li key={d}>{d}</li>)}</ul></> : null}
        {m.replaces && <p><b>Replaces:</b> {m.replaces}</p>}
        <h3>Stages</h3><p>{spec.stages.map((s) => s.name).join(" → ")}</p>
        <h3>Fields</h3><p>{spec.fields.map((f) => f.label).join(", ")}</p>
        <p className="muted" style={{ marginBottom: 0 }}>This is a first version. Stages and fields are easy to change, so say what's missing or wrong.</p>
      </div>
    </details>
  );
}

// ----- new record -----
export function SimpleAppNew() {
  const { id = "" } = useParams();
  const spec = findSpec(id);
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries((spec?.fields ?? []).filter((f) => f.default !== undefined).map((f) => [f.key, String(f.default)])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!spec) return <ModulePage />;
  const m = findModule(spec.id)!.module;

  async function submit(e?: FormEvent) {
    e?.preventDefault(); if (!spec) return;
    if (!title.trim()) { setError(new Error(`Fill in ${spec.title.toLowerCase()}.`)); return; }
    const missing = spec.fields.find((f) => f.required && !(vals[f.key] ?? "").trim());
    if (missing) { setError(new Error(`Fill in ${missing.label.toLowerCase()}.`)); return; }
    // The limits the app documentation gives each field: length, lowest and highest.
    for (const f of spec.fields) {
      const raw = (vals[f.key] ?? "").trim();
      if (!raw) continue;
      if (f.maxLength && raw.length > f.maxLength) { setError(new Error(`${f.label} takes ${f.maxLength} characters at most.`)); return; }
      const n = Number(raw);
      if (["number", "money", "percent"].includes(f.type) && Number.isFinite(n)) {
        if (f.min !== undefined && n < f.min) { setError(new Error(`${f.label} must be ${f.min} or more.`)); return; }
        if (f.max !== undefined && n > f.max) { setError(new Error(`${f.label} must be ${f.max} or less.`)); return; }
      }
    }
    setSaving(true); setError(null);
    try {
      const fields: Record<string, Value> = {};
      for (const f of spec.fields) if ((vals[f.key] ?? "") !== "") fields[f.key] = toValue(f, vals[f.key]);
      const r = await appsApi.create(spec.id, { prefix: spec.prefix, title: title.trim(), stage: spec.stages[0].id, owner: owner.trim() || undefined, fields });
      nav(`/m/${spec.id}/${r.number}`);
    } catch (err) { setError(err); setSaving(false); }
  }

  return (
    <>
      <CommandBar status={newLabel(spec.noun)}>
        <button className="cmd primary" disabled={saving} onClick={() => submit()}><Icon name="plus" size={14} />{saving ? "Saving…" : "Save"}</button>
        <button className="cmd" onClick={() => nav(-1)}>Discard</button>
      </CommandBar>
      <div className="page">
        <div className="page-title">
          <nav className="crumbs" aria-label="Breadcrumb" style={{ flexBasis: "100%", margin: 0 }}><Link to={`/m/${spec.id}`}>{m.name}</Link><span>/</span><span>New</span></nav>
          <h1>{newLabel(spec.noun)}</h1><span className="sub">Starts at {spec.stages[0].name}.</span>
        </div>
        <form onSubmit={submit} className="card form-card">
          <div className="card-body">
            <ErrorBox error={error} />
            <div className="field"><label htmlFor="f-title">{spec.title} <span className="muted">*</span></label>
              <input id="f-title" className="input" maxLength={200} autoFocus placeholder={spec.titleHint} value={title} onChange={(e) => setTitle(e.target.value)} /></div>
            <div className="sa-grid">
              {spec.fields.map((f) => (
                <div key={f.key} className={f.type === "long" ? "sa-wide" : ""}>
                  <FieldRow f={f}><Input id={`f-${f.key}`} f={f} value={vals[f.key] ?? ""} onChange={(v) => setVals((x) => ({ ...x, [f.key]: v }))} /></FieldRow>
                </div>
              ))}
              <div><div className="field"><label htmlFor="f-owner">Owner</label><input id="f-owner" className="input" placeholder="Name" value={owner} onChange={(e) => setOwner(e.target.value)} /></div></div>
            </div>
            <div className="btn-row" style={{ marginTop: 8 }}>
              <button type="submit" className="btn primary" disabled={saving}>{saving ? "Saving…" : `Create ${spec.noun}`}</button>
              <button type="button" className="btn" onClick={() => nav(-1)}>Cancel</button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

// ----- record page -----
export function SimpleAppRecord() {
  const { id = "", number = "" } = useParams();
  const spec = findSpec(id);
  const { data: rec, error, reload } = useLoad(() => appsApi.get(number), [number]);
  const [edit, setEdit] = useState<Record<string, string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [comment, setComment] = useState("");
  if (!spec) return <ModulePage />;
  const m = findModule(spec.id)!.module;
  if (error) return <div className="page"><ErrorBox error={error} /><Link to={`/m/${spec.id}`}>Back to {m.name}</Link></div>;
  if (!rec) return <div className="page"><div className="card empty">Loading…</div></div>;

  const run = async (fn: () => Promise<unknown>) => { setBusy(true); setErr(null); try { await fn(); await reload(); } catch (e) { setErr(e); } finally { setBusy(false); } };
  const startEdit = () => setEdit({ title: rec.title, owner: rec.owner ?? "", ...Object.fromEntries(spec.fields.map((f) => [f.key, toRaw(f, rec.fields[f.key] ?? null)])) });
  const save = () => run(async () => {
    if (!edit) return;
    const fields: Record<string, Value> = {};
    for (const f of spec.fields) fields[f.key] = toValue(f, edit[f.key] ?? "");
    await appsApi.update(rec.number, { title: edit.title, owner: edit.owner, fields });
    setEdit(null);
  });
  const idx = spec.stages.findIndex((s) => s.id === rec.stage);
  const entered = (sid: string) => [...rec.events].reverse().find((e) => e.kind === "stage" && e.detail?.to === sid)?.at ?? (sid === spec.stages[0].id ? rec.createdAt : undefined);
  const last = spec.stages.length - 1;

  return (
    <>
      <CommandBar status={`${m.name} · ${stageOf(spec, rec.stage).name}`}>
        {edit ? <>
          <button className="cmd primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
          <button className="cmd" onClick={() => setEdit(null)}>Cancel</button>
        </> : <button className="cmd" onClick={startEdit}>Edit</button>}
        {!edit && idx >= 0 && idx < last && <button className="cmd" disabled={busy} onClick={() => run(() => appsApi.update(rec.number, { stage: spec.stages[idx + 1].id }))}>Move to {spec.stages[idx + 1].name}</button>}
        <Link className="cmd" to={`/m/${spec.id}`}>← {m.name}</Link>
      </CommandBar>
      <div className="page">
        <div className="page-title">
          <nav className="crumbs" aria-label="Breadcrumb" style={{ flexBasis: "100%", margin: 0 }}><Link to={`/m/${spec.id}`}>{m.name}</Link><span>/</span><span>{rec.number}</span></nav>
          {edit ? <input className="input sa-title" aria-label={spec.title} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /> : <h1>{rec.title}</h1>}
          <span className="sub mono">{rec.number}</span>
        </div>
        <div className="bpf" role="list" aria-label="Stages">
          {spec.stages.map((s, i) => {
            const current = s.id === rec.stage; const done = idx >= 0 && i < idx;
            const when = entered(s.id);
            return (
              <button key={s.id} role="listitem" className={`bpf-stage ${current ? "current" : done ? "done" : "next"}`} disabled={busy || current}
                onClick={() => run(() => appsApi.update(rec.number, { stage: s.id }))} title={current ? s.name : `Move to ${s.name}`} aria-current={current ? "step" : undefined}>
                <span className="name">{done ? "✓ " : ""}{s.name}</span>
                <span className="meta">{current ? (when ? `Since ${ago(when)}` : "Current") : done && when ? ago(when) : "Move here"}</span>
              </button>
            );
          })}
        </div>
        <ErrorBox error={err} />
        <div className="grid cols-main">
          <section className="card">
            <div className="card-head"><h2>Details</h2></div>
            <div className="card-body">
              {edit ? (
                <div className="sa-grid">
                  {spec.fields.map((f) => (
                    <div key={f.key} className={f.type === "long" ? "sa-wide" : ""}>
                      <FieldRow f={f}><Input id={`f-${f.key}`} f={f} value={edit[f.key] ?? ""} onChange={(v) => setEdit({ ...edit, [f.key]: v })} /></FieldRow>
                    </div>
                  ))}
                  <div><div className="field"><label htmlFor="f-owner">Owner</label><input id="f-owner" className="input" value={edit.owner} onChange={(e) => setEdit({ ...edit, owner: e.target.value })} /></div></div>
                </div>
              ) : (
                <dl className="sa-dl">
                  {spec.fields.map((f) => <div key={f.key} className={f.type === "long" ? "sa-wide" : ""}><dt>{f.label}</dt><dd>{show(f, rec.fields[f.key] ?? null)}</dd></div>)}
                  <div><dt>Owner</dt><dd>{rec.owner ?? <span className="muted">Unassigned</span>}</dd></div>
                  <div><dt>Created</dt><dd>{rec.createdByName ?? rec.createdBy} · {ago(rec.createdAt)}</dd></div>
                </dl>
              )}
            </div>
          </section>
          <section className="card">
            <div className="card-head"><h2>Activity</h2></div>
            <div className="card-body">
              <form onSubmit={(e) => { e.preventDefault(); if (comment.trim()) run(async () => { await appsApi.comment(rec.number, comment.trim()); setComment(""); }); }} style={{ marginBottom: 16 }}>
                <textarea className="textarea" rows={2} aria-label="Comment" placeholder="Add a note" value={comment} onChange={(e) => setComment(e.target.value)} />
                <button className="btn" style={{ marginTop: 6 }} disabled={busy || !comment.trim()}>Comment</button>
              </form>
              <ul className="timeline">
                {[...rec.events].reverse().map((e) => <li key={e.id}><div>{describe(spec, e)}</div><div className="when">{e.actorName ?? "Someone"} · {ago(e.at)}</div></li>)}
              </ul>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function describe(spec: AppSpec, e: AppEvent): ReactNode {
  if (e.kind === "created") return <>Created {spec.noun}</>;
  if (e.kind === "comment") return <span style={{ whiteSpace: "pre-wrap" }}>{e.body}</span>;
  if (e.kind === "stage") return <>Moved to <b>{stageOf(spec, e.detail.to).name}</b>{e.body ? ` · ${e.body}` : ""}</>;
  const keys = Object.keys(e.detail?.changed ?? {});
  const names = keys.map((k) => (k === "title" ? spec.title : k === "owner" ? "Owner" : spec.fields.find((f) => f.key === k)?.label ?? k));
  return <>Changed {names.join(", ") || "details"}{keys.length === 1 && keys[0] !== "title" ? <> to <b>{show(keys[0] === "owner" ? { key: "owner", label: "Owner", type: "person" } : spec.fields.find((f) => f.key === keys[0]), e.detail.changed[keys[0]][1])}</b></> : null}</>;
}

